import React, { useState, useEffect } from 'react';
import { Users, Clock, CheckCircle, XCircle, AlertTriangle, TrendingUp, Filter, Download, FileText, BarChart, Search, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { User } from '../../types';
import Avatar from '../common/Avatar';
import { todayLocal, toLocalDateString } from '../../utils/localDate';
import { toast } from '../../utils/toast';
import { readApiError } from '../common/apiError';

// GET /api/users returns realmRoles/roles arrays; `role` alone is only the primary role.
const userRoles = (u: any): string[] =>
  ((u?.realmRoles || u?.roles || [u?.role]) as unknown[])
    .filter((r): r is string => typeof r === 'string')
    .map(r => r.toLowerCase());
const hasRole = (u: any, role: string) => userRoles(u).includes(role);

interface TeamTimesheetsProps {
  currentUser: User;
}

const TeamTimesheets: React.FC<TeamTimesheetsProps> = ({ currentUser }) => {
  const [timesheets, setTimesheets] = useState<any[]>([]);
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({
    startDate: toLocalDateString(new Date(new Date().setMonth(new Date().getMonth() - 1))),
    endDate: todayLocal(),
    status: 'all',
    userId: 'all'
  });
  const [reviewingEntry, setReviewingEntry] = useState<any>(null);
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject' | 'request_revision'>('approve');
  const [reviewComments, setReviewComments] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [selectedEntries, setSelectedEntries] = useState<string[]>([]);
  const [showAnalytics, setShowAnalytics] = useState(true);
  const [sortBy, setSortBy] = useState<'date' | 'hours' | 'name'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const isAdmin = hasRole(currentUser, 'admin');

  // Team members only depend on who is logged in — don't refetch on every filter change
  useEffect(() => {
    loadTeamMembers();
  }, [currentUser.id]);

  useEffect(() => {
    loadTimesheets();
  }, [currentUser.id, filter]);

  const loadTeamMembers = async () => {
    try {
      const response = await fetch('/api/users');
      if (response.ok) {
        const data = await response.json();
        const users: any[] = Array.isArray(data) ? data : [];
        // Filter to show team members based on current user's role
        let members;

        if (isAdmin) {
          // Admin sees everyone's timesheets
          members = users.filter((u: any) => u.id !== currentUser.id);
        } else if (currentUser.role === 'devops') {
          // DevOps TL sees other DevOps members (excluding self)
          members = users.filter((u: any) =>
            hasRole(u, 'devops') && u.id !== currentUser.id
          );
        } else {
          // TLs/PMs see developers, testers, interns, and junior TLs
          members = users.filter((u: any) =>
            ['developer', 'tester', 'intern', 'devops'].some(r => hasRole(u, r)) ||
            (hasRole(u, 'tl') && u.id !== currentUser.id)
          );
        }
        
        setTeamMembers(members);
      }
    } catch (err) {
      console.error('Error loading team members:', err);
    }
  };

  const loadTimesheets = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        startDate: filter.startDate,
        endDate: filter.endDate,
        ...(filter.status !== 'all' && { status: filter.status }),
        ...(filter.userId !== 'all' && { userId: filter.userId })
      });

      // Admin: all users' timesheets (admin-only endpoint); others: their team
      const url = isAdmin
        ? `/api/timesheets?${params}`
        : `/api/timesheets/team/${currentUser.id}?${params}`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setTimesheets(Array.isArray(data) ? data : []);
      } else {
        toast(await readApiError(response, 'Failed to load timesheets'), 'error');
      }
    } catch (err) {
      console.error('Error loading team timesheets:', err);
      toast('Failed to load timesheets', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async () => {
    if (!reviewingEntry) return;

    try {
      const response = await fetch(`/api/timesheets/${reviewingEntry._id}/review`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: reviewAction,
          reviewComments: reviewComments.trim(),
          reviewedBy: {
            id: currentUser.id,
            name: currentUser.name,
            role: currentUser.role
          }
        })
      });

      if (response.ok) {
        toast(`✅ Timesheet ${reviewAction === 'approve' ? 'approved' : reviewAction === 'reject' ? 'rejected' : 'marked for revision'} successfully`);
        setReviewingEntry(null);
        setReviewComments('');
        setReviewAction('approve');
        loadTimesheets();
      } else {
        toast(`❌ ${await readApiError(response, 'Failed to review timesheet')}`, 'error');
      }
    } catch (err) {
      toast('❌ Failed to review timesheet', 'error');
    }
  };

  // Export to CSV — each field is quoted and internal quotes escaped, so
  // work descriptions containing commas, quotes, or newlines (common, since
  // the field is a multi-line textarea) don't corrupt the row structure.
  const csvField = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;

  const exportToCSV = () => {
    const headers = ['Date', 'Team Member', 'Role', 'Hours', 'Status', 'Work Description', 'Projects'];
    const rows = timesheets.map(entry => [
      entry.date,
      entry.userName,
      entry.userRole,
      entry.hoursWorked,
      entry.status,
      entry.workDescription,
      (entry.projects || []).join('; ')
    ]);

    const csvContent = [
      headers.map(csvField).join(','),
      ...rows.map(row => row.map(csvField).join(','))
    ].join('\r\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `team-timesheets-${filter.startDate}-to-${filter.endDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Bulk approve selected entries
  const bulkApprove = async () => {
    if (selectedEntries.length === 0) {
      toast('Please select entries to approve');
      return;
    }
    
    if (!confirm(`Approve ${selectedEntries.length} selected entries?`)) return;
    
    try {
      // fetch() only rejects on a network failure — a 4xx/5xx response
      // still resolves "successfully", so Promise.all alone can't tell a
      // partial failure from full success. Check response.ok per request.
      const results = await Promise.all(
        selectedEntries.map(async (id) => {
          const response = await fetch(`/api/timesheets/${id}/review`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              action: 'approve',
              reviewComments: 'Bulk approved',
              reviewedBy: {
                id: currentUser.id,
                name: currentUser.name,
                role: currentUser.role
              }
            })
          });
          return { id, ok: response.ok };
        })
      );

      const failed = results.filter(r => !r.ok);
      if (failed.length === 0) {
        toast(`✅ ${selectedEntries.length} entries approved successfully`);
      } else {
        toast(`⚠️ ${results.length - failed.length} approved, ${failed.length} failed. Please retry the failed entries.`);
      }
      setSelectedEntries(failed.map(f => f.id));
      loadTimesheets();
    } catch (err) {
      toast('❌ Failed to bulk approve');
    }
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-800 border-amber-300',
      approved: 'bg-emerald-100 text-emerald-800 border-emerald-300',
      rejected: 'bg-rose-100 text-rose-800 border-rose-300',
      revised: 'bg-sky-100 text-sky-800 border-sky-300'
    };

    const icons = {
      pending: <AlertTriangle className="w-4 h-4" />,
      approved: <CheckCircle className="w-4 h-4" />,
      rejected: <XCircle className="w-4 h-4" />,
      revised: <AlertTriangle className="w-4 h-4" />
    };

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium border ${styles[status as keyof typeof styles]}`}>
        {icons[status as keyof typeof icons]}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Group timesheets by user
  const groupedTimesheets = timesheets.reduce((acc, entry) => {
    const userId = entry.userId;
    if (!acc[userId]) {
      acc[userId] = {
        user: {
          id: entry.userId,
          name: entry.userName,
          email: entry.userEmail,
          role: entry.userRole
        },
        entries: [],
        totalHours: 0,
        avgHours: 0,
        daysLogged: 0,
        pendingCount: 0,
        approvedCount: 0
      };
    }
    acc[userId].entries.push(entry);
    acc[userId].totalHours += entry.hoursWorked;
    acc[userId].daysLogged++;
    if (entry.status === 'pending' || entry.status === 'revised') acc[userId].pendingCount++;
    if (entry.status === 'approved') acc[userId].approvedCount++;
    return acc;
  }, {} as any);

  // Calculate avg hours
  Object.values(groupedTimesheets).forEach((group: any) => {
    group.avgHours = group.totalHours / group.daysLogged;
  });

  const teamStats = {
    totalMembers: Object.keys(groupedTimesheets).length,
    totalHours: Object.values(groupedTimesheets).reduce((sum: number, g: any) => sum + g.totalHours, 0),
    pendingReviews: Object.values(groupedTimesheets).reduce((sum: number, g: any) => sum + g.pendingCount, 0),
    avgHoursPerMember: Object.keys(groupedTimesheets).length > 0 
      ? Object.values(groupedTimesheets).reduce((sum: number, g: any) => sum + g.totalHours, 0) / Object.keys(groupedTimesheets).length
      : 0
  };

  // Filter and sort grouped timesheets
  let filteredGroups = Object.values(groupedTimesheets);
  
  // Apply search filter
  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase();
    filteredGroups = filteredGroups.filter((group: any) =>
      group.user.name.toLowerCase().includes(query) ||
      group.user.email.toLowerCase().includes(query) ||
      group.entries.some((entry: any) => 
        entry.workDescription.toLowerCase().includes(query) ||
        (entry.projects && entry.projects.some((p: string) => p.toLowerCase().includes(query)))
      )
    );
  }
  
  // Sort groups
  filteredGroups.sort((a: any, b: any) => {
    let comparison = 0;
    if (sortBy === 'name') {
      comparison = a.user.name.localeCompare(b.user.name);
    } else if (sortBy === 'hours') {
      comparison = a.totalHours - b.totalHours;
    } else {
      // Sort by most recent date
      const aMaxDate = Math.max(...a.entries.map((e: any) => new Date(e.date).getTime()));
      const bMaxDate = Math.max(...b.entries.map((e: any) => new Date(e.date).getTime()));
      comparison = aMaxDate - bMaxDate;
    }
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  return (
    <div className="space-y-6">
      {/* Header with Quick Stats */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl shadow-lg p-6 text-white">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-3xl font-bold">Team Timesheets Dashboard</h1>
            <p className="text-blue-100 mt-1">Monitor and manage your team's work hours</p>
          </div>
          <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2">
            <Users className="w-5 h-5" />
            <span className="font-semibold">{teamStats.totalMembers} Team Members</span>
          </div>
        </div>
        
        {/* Quick Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
            <div className="flex items-center gap-3">
              <div className="bg-green-500 rounded-lg p-3">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-blue-100 text-sm">Total Hours</p>
                <p className="text-2xl font-bold">{teamStats.totalHours.toFixed(1)}h</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
            <div className="flex items-center gap-3">
              <div className="bg-yellow-500 rounded-lg p-3">
                <AlertTriangle className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-blue-100 text-sm">Pending Reviews</p>
                <p className="text-2xl font-bold">{teamStats.pendingReviews}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
            <div className="flex items-center gap-3">
              <div className="bg-purple-500 rounded-lg p-3">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-blue-100 text-sm">Avg Hours/Member</p>
                <p className="text-2xl font-bold">{teamStats.avgHoursPerMember.toFixed(1)}h</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
            <div className="flex items-center gap-3">
              <div className="bg-blue-500 rounded-lg p-3">
                <BarChart className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-blue-100 text-sm">Active Entries</p>
                <p className="text-2xl font-bold">{timesheets.length}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Action Toolbar */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* Left Side - Search and View Options */}
          <div className="flex items-center gap-3">
            {/* Search Box */}
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search team member or work..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 w-64"
              />
            </div>

            {/* View Mode Toggle */}
            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('cards')}
                className={`px-3 py-2 text-sm font-medium ${
                  viewMode === 'cards'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Users className="w-4 h-4 inline mr-1" />
                Cards
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-2 text-sm font-medium ${
                  viewMode === 'table'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <FileText className="w-4 h-4 inline mr-1" />
                Table
              </button>
            </div>
          </div>

          {/* Right Side - Actions */}
          <div className="flex items-center gap-2">
            {/* Analytics Toggle */}
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
            >
              <BarChart className="w-4 h-4 inline mr-1" />
              {showAnalytics ? 'Hide' : 'Show'} Analytics
            </button>

            {/* Bulk Approve (show if entries selected) */}
            {selectedEntries.length > 0 && (
              <button
                onClick={bulkApprove}
                className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
              >
                <CheckCircle className="w-4 h-4 inline mr-1" />
                Approve {selectedEntries.length} Selected
              </button>
            )}

            {/* Export Button */}
            <button
              onClick={exportToCSV}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
            >
              <Download className="w-4 h-4 inline mr-1" />
              Export CSV
            </button>

            {/* Refresh Button */}
            <button
              onClick={loadTimesheets}
              className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Sort Options */}
        <div className="mt-3 pt-3 border-t flex items-center gap-4">
          <span className="text-sm font-medium text-gray-700">Sort by:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'date' | 'hours' | 'name')}
            className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
          >
            <option value="date">Date</option>
            <option value="hours">Hours Worked</option>
            <option value="name">Team Member Name</option>
          </select>
          
          <button
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="px-2 py-1 border border-gray-300 rounded-lg hover:bg-gray-50"
            title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
          >
            {sortOrder === 'asc' ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>

          {selectedEntries.length > 0 && (
            <span className="ml-auto text-sm text-blue-600 font-medium">
              {selectedEntries.length} entries selected
            </span>
          )}
        </div>
      </div>

      {/* Analytics Panel (collapsible) */}
      {showAnalytics && (
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold text-gray-800 flex items-center">
              <BarChart className="w-6 h-6 mr-2 text-blue-600" />
              Team Performance Analytics
            </h3>
            <button
              onClick={() => setShowAnalytics(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <ChevronUp className="w-5 h-5" />
            </button>
          </div>
          
          {/* Primary Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {/* Target Achievement Rate */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-lg p-4 border border-green-200">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-green-700 uppercase font-semibold">Target Achievement</p>
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <p className="text-3xl font-bold text-green-700">
                {((timesheets.filter(t => t.hoursWorked >= 7.5).length / Math.max(timesheets.length, 1)) * 100).toFixed(0)}%
              </p>
              <p className="text-xs text-green-600 mt-2">
                {timesheets.filter(t => t.hoursWorked >= 7.5).length} / {timesheets.length} days ≥7.5h
              </p>
              <div className="mt-2 bg-green-200 rounded-full h-2">
                <div 
                  className="bg-green-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${((timesheets.filter(t => t.hoursWorked >= 7.5).length / Math.max(timesheets.length, 1)) * 100)}%` }}
                ></div>
              </div>
            </div>

            {/* Approval Rate */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-lg p-4 border border-blue-200">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-blue-700 uppercase font-semibold">Approval Rate</p>
                <CheckCircle className="w-5 h-5 text-blue-600" />
              </div>
              <p className="text-3xl font-bold text-blue-700">
                {((timesheets.filter(t => t.status === 'approved').length / Math.max(timesheets.length, 1)) * 100).toFixed(0)}%
              </p>
              <p className="text-xs text-blue-600 mt-2">
                {timesheets.filter(t => t.status === 'approved').length} approved entries
              </p>
              <div className="mt-2 bg-blue-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${((timesheets.filter(t => t.status === 'approved').length / Math.max(timesheets.length, 1)) * 100)}%` }}
                ></div>
              </div>
            </div>

            {/* Peak Performance Day */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-100 rounded-lg p-4 border border-purple-200">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-purple-700 uppercase font-semibold">Best Day</p>
                <TrendingUp className="w-5 h-5 text-purple-600" />
              </div>
              <p className="text-3xl font-bold text-purple-700">
                {timesheets.length > 0 
                  ? new Date(timesheets.reduce((max, t) => t.hoursWorked > max.hoursWorked ? t : max, timesheets[0]).date).toLocaleDateString('en-US', { weekday: 'short' })
                  : 'N/A'}
              </p>
              <p className="text-xs text-purple-600 mt-2">
                {timesheets.length > 0 
                  ? `${timesheets.reduce((max, t) => t.hoursWorked > max.hoursWorked ? t : max, timesheets[0]).hoursWorked}h logged`
                  : 'No data'}
              </p>
            </div>

            {/* Pending Reviews */}
            <div className="bg-gradient-to-br from-yellow-50 to-orange-100 rounded-lg p-4 border border-yellow-200">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-yellow-700 uppercase font-semibold">Pending Reviews</p>
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
              </div>
              <p className="text-3xl font-bold text-yellow-700">{teamStats.pendingReviews}</p>
              <p className="text-xs text-yellow-600 mt-2">
                {timesheets.filter(t => t.status === 'revised').length} revisions requested
              </p>
            </div>
          </div>

          {/* Secondary Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {/* Average Hours Trend */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="text-sm font-semibold text-gray-700 mb-2">Average Daily Hours</p>
              <p className="text-2xl font-bold text-gray-800">
                {(timesheets.reduce((sum, t) => sum + t.hoursWorked, 0) / Math.max(timesheets.length, 1)).toFixed(1)}h
              </p>
              <p className="text-xs text-gray-500 mt-1">Across all team members</p>
            </div>

            {/* Completion Rate */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="text-sm font-semibold text-gray-700 mb-2">Completion Rate</p>
              <p className="text-2xl font-bold text-gray-800">
                {((timesheets.filter(t => t.status !== 'pending').length / Math.max(timesheets.length, 1)) * 100).toFixed(0)}%
              </p>
              <p className="text-xs text-gray-500 mt-1">Reviewed entries</p>
            </div>

            {/* Total Projects */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="text-sm font-semibold text-gray-700 mb-2">Active Projects</p>
              <p className="text-2xl font-bold text-gray-800">
                {new Set(timesheets.flatMap(t => t.projects || [])).size}
              </p>
              <p className="text-xs text-gray-500 mt-1">Unique projects tracked</p>
            </div>
          </div>

          {/* Top Performers */}
          <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg p-4 border border-indigo-200">
            <p className="text-sm font-bold text-gray-800 mb-3 flex items-center">
              <TrendingUp className="w-4 h-4 mr-2 text-indigo-600" />
              Top Performers This Period
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {Object.values(groupedTimesheets)
                .sort((a: any, b: any) => b.totalHours - a.totalHours)
                .slice(0, 3)
                .map((member: any, index: number) => (
                  <div key={member.user.id} className="bg-white rounded-lg p-3 shadow-sm border border-indigo-100">
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                        index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : 'bg-orange-400'
                      }`}>
                        {index + 1}
                      </div>
                      <Avatar name={member.user.name} userId={member.user.id} size="xs" />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-800 truncate">{member.user.name}</p>
                        <p className="text-xs text-gray-500">{member.user.role.toUpperCase()}</p>
                      </div>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">{member.totalHours.toFixed(1)}h total</span>
                      <span className="text-green-600 font-semibold">{member.avgHours.toFixed(1)}h avg</span>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-600" />
            <h3 className="font-medium text-gray-800">Filters</h3>
          </div>
          {/* Quick Date Filter Buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => {
                const today = new Date();
                const lastWeek = new Date(today);
                lastWeek.setDate(today.getDate() - 7);
                setFilter({
                  ...filter,
                  startDate: toLocalDateString(lastWeek),
                  endDate: toLocalDateString(today)
                });
              }}
              className="px-3 py-1 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Last 7 Days
            </button>
            <button
              onClick={() => {
                const today = new Date();
                const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
                setFilter({
                  ...filter,
                  startDate: toLocalDateString(firstDay),
                  endDate: toLocalDateString(today)
                });
              }}
              className="px-3 py-1 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              This Month
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Team Member</label>
            <select
              value={filter.userId}
              onChange={(e) => setFilter({ ...filter, userId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Members</option>
              {teamMembers.map(member => (
                <option key={member.id || member._id} value={member.id || member._id}>
                  {member.name || member.username}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={filter.startDate}
              onChange={(e) => setFilter({ ...filter, startDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={filter.endDate}
              onChange={(e) => setFilter({ ...filter, endDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filter.status}
              onChange={(e) => setFilter({ ...filter, status: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="revised">Needs Revision</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={loadTimesheets}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Apply
            </button>
          </div>
        </div>
      </div>

      {/* Team Members Summary Cards */}
      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : filteredGroups.length === 0 ? (
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No timesheets found for the selected filters</p>
          {searchQuery && (
            <p className="text-sm text-gray-500 mt-2">
              Try adjusting your search term: "{searchQuery}"
            </p>
          )}
        </div>
      ) : viewMode === 'table' ? (
        // TABLE VIEW
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Select</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Team Member</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Hours</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Work Description</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Projects</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredGroups.map((group: any) =>
                  group.entries.map((entry: any) => (
                    <tr key={entry._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        {(entry.status === 'pending' || entry.status === 'revised') && (
                          <input
                            type="checkbox"
                            checked={selectedEntries.includes(entry._id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedEntries([...selectedEntries, entry._id]);
                              } else {
                                setSelectedEntries(selectedEntries.filter(id => id !== entry._id));
                              }
                            }}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Avatar name={group.user.name} userId={group.user.id} size="sm" />
                          <div>
                            <div className="font-medium text-gray-900">{group.user.name}</div>
                            <div className="text-xs text-gray-500">{group.user.role.toUpperCase()}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-sm font-semibold ${
                          entry.hoursWorked >= 7.5 ? 'text-green-600' : 'text-yellow-600'
                        }`}>
                          {entry.hoursWorked}h
                          {entry.hoursWorked >= 7.5 ? ' ✓' : ' ⚠'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(entry.status)}
                      </td>
                      <td className="px-4 py-3 max-w-xs">
                        <p className="text-sm text-gray-700 truncate" title={entry.workDescription}>
                          {entry.workDescription}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        {entry.projects && entry.projects.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {entry.projects.slice(0, 2).map((project: string, i: number) => (
                              <span key={i} className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs">
                                {project}
                              </span>
                            ))}
                            {entry.projects.length > 2 && (
                              <span className="text-xs text-gray-500">+{entry.projects.length - 2}</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {(entry.status === 'pending' || entry.status === 'revised') && (
                          <button
                            onClick={() => setReviewingEntry(entry)}
                            className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium"
                          >
                            Review
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        // CARDS VIEW
        <div className="space-y-6">
          {filteredGroups.map((group: any) => (
            <div key={group.user.id} className="bg-white rounded-lg shadow-md p-6">
              {/* Member Header */}
              <div className="flex justify-between items-start mb-4 pb-4 border-b">
                <div className="flex items-center gap-3">
                  <Avatar name={group.user.name} userId={group.user.id} size="md" />
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">{group.user.name}</h3>
                    <p className="text-sm text-gray-600">{group.user.email} • {group.user.role.toUpperCase()}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Period Summary</p>
                  <div className="flex items-center gap-4 mt-1">
                    <span className="text-sm">
                      <strong>{group.totalHours.toFixed(1)}h</strong> total
                    </span>
                    <span className="text-sm">
                      <strong>{group.avgHours.toFixed(1)}h</strong> avg
                    </span>
                    <span className="text-sm">
                      <strong>{group.daysLogged}</strong> days
                    </span>
                  </div>
                  {group.pendingCount > 0 && (
                    <span className="inline-block mt-2 px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-medium">
                      {group.pendingCount} pending review
                    </span>
                  )}
                </div>
              </div>

              {/* Daily Summary Grid - Quick 7.5h Target View */}
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Daily Hours Overview (✓ = Met 7.5h Target)</h4>
                <div className="grid grid-cols-7 gap-2">
                  {group.entries.sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 7).reverse().map((entry: any) => {
                    const dayName = new Date(entry.date).toLocaleDateString('en-US', { weekday: 'short' });
                    const dayDate = new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    const meetsTarget = entry.hoursWorked >= 7.5;
                    return (
                      <div
                        key={entry._id}
                        className={`p-2 rounded-lg text-center ${
                          meetsTarget 
                            ? 'bg-green-100 border-2 border-green-500' 
                            : 'bg-yellow-100 border-2 border-yellow-500'
                        }`}
                      >
                        <div className="text-xs font-semibold text-gray-700">{dayName}</div>
                        <div className="text-xs text-gray-600">{dayDate}</div>
                        <div className={`text-lg font-bold mt-1 ${meetsTarget ? 'text-green-700' : 'text-yellow-700'}`}>
                          {entry.hoursWorked}h
                        </div>
                        <div className="text-lg">
                          {meetsTarget ? '✓' : '⚠'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Entries */}
              <div className="space-y-3">
                {group.entries.map((entry: any) => (
                  <div key={entry._id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-4">
                        {/* Selection Checkbox (only for pending/revised) */}
                        {(entry.status === 'pending' || entry.status === 'revised') && (
                          <input
                            type="checkbox"
                            checked={selectedEntries.includes(entry._id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedEntries([...selectedEntries, entry._id]);
                              } else {
                                setSelectedEntries(selectedEntries.filter(id => id !== entry._id));
                              }
                            }}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                        )}
                        
                        <span className="font-medium text-gray-800">
                          {new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                        {getStatusBadge(entry.status)}
                        <span className="flex items-center gap-1 text-sm text-gray-600">
                          <Clock className="w-4 h-4" />
                          {entry.hoursWorked}h
                          {entry.hoursWorked >= 7.5 ? (
                            <span className="text-green-600">✓</span>
                          ) : (
                            <span className="text-yellow-600">⚠</span>
                          )}
                        </span>
                      </div>

                      {(entry.status === 'pending' || entry.status === 'revised') && (
                        <button
                          onClick={() => setReviewingEntry(entry)}
                          className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                        >
                          Review
                        </button>
                      )}
                    </div>

                    <div className="space-y-2">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Work Description:</p>
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">{entry.workDescription}</p>
                      </div>

                      {entry.projects && entry.projects.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {entry.projects.map((project: string, idx: number) => (
                            <span key={idx} className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs">
                              {project}
                            </span>
                          ))}
                        </div>
                      )}

                      {entry.taskLinks && entry.taskLinks.length > 0 && (
                        <div className="space-y-1">
                          {entry.taskLinks.map((link: any, idx: number) => (
                            <a
                              key={idx}
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block text-blue-600 hover:underline text-xs"
                            >
                              🔗 {link.title}
                            </a>
                          ))}
                        </div>
                      )}

                      {entry.reviewComments && (
                        <div className="mt-2 p-2 bg-gray-50 rounded text-sm">
                          <p className="font-medium text-gray-700">Review:</p>
                          <p className="text-gray-600">{entry.reviewComments}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Review Modal */}
      {reviewingEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Review Timesheet</h2>

            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">
                <strong>{reviewingEntry.userName}</strong> • {new Date(reviewingEntry.date).toLocaleDateString()}
              </p>
              <p className="text-sm text-gray-600">
                <Clock className="w-4 h-4 inline mr-1" />
                {reviewingEntry.hoursWorked} hours
              </p>
              <div className="mt-3">
                <p className="text-sm font-medium text-gray-700 mb-1">Work Description:</p>
                <p className="text-sm text-gray-600 whitespace-pre-wrap">{reviewingEntry.workDescription}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Action</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setReviewAction('approve')}
                    className={`flex-1 px-4 py-2 rounded-lg border-2 font-medium ${
                      reviewAction === 'approve'
                        ? 'bg-green-600 text-white border-green-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-green-600'
                    }`}
                  >
                    <CheckCircle className="w-4 h-4 inline mr-1" />
                    Approve
                  </button>
                  <button
                    onClick={() => setReviewAction('request_revision')}
                    className={`flex-1 px-4 py-2 rounded-lg border-2 font-medium ${
                      reviewAction === 'request_revision'
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-blue-600'
                    }`}
                  >
                    <AlertTriangle className="w-4 h-4 inline mr-1" />
                    Request Revision
                  </button>
                  <button
                    onClick={() => setReviewAction('reject')}
                    className={`flex-1 px-4 py-2 rounded-lg border-2 font-medium ${
                      reviewAction === 'reject'
                        ? 'bg-red-600 text-white border-red-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-red-600'
                    }`}
                  >
                    <XCircle className="w-4 h-4 inline mr-1" />
                    Reject
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comments {reviewAction !== 'approve' && <span className="text-red-600">*</span>}
                </label>
                <textarea
                  value={reviewComments}
                  onChange={(e) => setReviewComments(e.target.value)}
                  rows={4}
                  placeholder={
                    reviewAction === 'approve'
                      ? 'Optional: Add comments about the work...'
                      : 'Please provide feedback or reasons...'
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setReviewingEntry(null);
                  setReviewComments('');
                  setReviewAction('approve');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleReview}
                disabled={reviewAction !== 'approve' && !reviewComments.trim()}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Submit Review
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamTimesheets;
